import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Radio, ArrowLeft, Plug, PlugZap, Trash2, Plus, Zap, Lightbulb } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import KempenvisionNav from '@/components/KempenvisionNav'
import { Button } from '../components/ui/button'
import { Badge } from '../components/ui/badge'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select'
import { useAuth } from '../hooks/useAuth'
import {
  ACTIONS, ACTION_GROUPS, openMIDI, listInputs, listOutputs, attachInputListener,
  eventKey, eventLabel, keyLabel, keyIsNote,
  loadMappings, saveMappings, loadInputId, saveInputId, loadOutputId, saveOutputId,
  normalizeMapping, LED_COLORS, LED_COLOR_KEYS, sendLED, clearAllLeds,
  DEVICE_MODELS, DEVICE_MODEL_KEYS, loadModel, saveModel, modelHasLeds,
} from '../lib/visionMidi'
import { useConfirm } from '../hooks/useConfirm'
import MidiSyncCard from '@/components/MidiSyncCard'
import { useMidiConfigSync } from '@/lib/midiSync'
import { useMidiContextIndicator } from '@/lib/midiContext'

export default function KempenvisionMidiPage() {
  useMidiContextIndicator('kempenvision')
  const confirm = useConfirm()
  const { user } = useAuth()
  const isAdmin = user?.role === 'superadmin' || user?.role === 'admin'
  const [access, setAccess]     = useState(null)
  const [inputs, setInputs]     = useState([])
  const [outputs, setOutputs]   = useState([])
  const [inputId, setInputId]   = useState(loadInputId())
  const [outputId, setOutputIdSt] = useState(loadOutputId())
  const [model, setModelSt] = useState(loadModel())
  const [lastEvent, setLastEvent] = useState(null)
  const [mappings, setMappings]   = useState(() => loadMappings())

  // Server-side config: mappings + model reizen mee naar elk scherm.
  // Device-ids blijven lokaal — die verschillen per machine.
  const _applyRemote = (remoteMappings, remoteModel) => {
    if (remoteMappings) { setMappings(remoteMappings); saveMappings(remoteMappings) }
    if (remoteModel)    { setModelSt(remoteModel);     saveModel(remoteModel) }
  }
  const midiSync = useMidiConfigSync('kempenvision', { mappings, model }, _applyRemote)
  const [error, setError] = useState(null)

  // Learn state
  const [learnMode, setLearnMode] = useState(false)
  const [captured, setCaptured]   = useState(null) // pending event to bind

  const detachRef = useRef(null)
  const outputRef = useRef(null)

  const openAccess = async () => {
    setError(null)
    try {
      const a = await openMIDI()
      setAccess(a)
      const ins = listInputs(a);   setInputs(ins)
      const outs = listOutputs(a); setOutputs(outs)
      if (!inputId && ins[0])   { setInputId(ins[0].id); saveInputId(ins[0].id) }
      if (!outputId && outs[0]) { setOutputIdSt(outs[0].id); saveOutputId(outs[0].id) }
      a.onstatechange = () => { setInputs(listInputs(a)); setOutputs(listOutputs(a)) }
    } catch (e) { setError(e.message) }
  }
  useEffect(() => { outputRef.current = outputs.find(o => o.id === outputId) || null }, [outputs, outputId])

  // Attach listener whenever access + input + mode changes
  useEffect(() => {
    if (detachRef.current) { detachRef.current(); detachRef.current = null }
    if (!access) return
    const input = inputs.find(i => i.id === inputId)
    if (!input) return
    detachRef.current = attachInputListener(input, (evt) => {
      setLastEvent(evt)
      const key = eventKey(evt)
      if (!key) return
      if (learnMode) {
        // Capture the event; user picks action next
        setCaptured({ key, evt })
      }
    })
    return () => { if (detachRef.current) { detachRef.current(); detachRef.current = null } }
  }, [access, inputId, inputs.length, learnMode])

  if (!isAdmin) return <div className="p-6 text-muted-foreground">Enkel voor admins.</div>

  const changeInput  = (id) => { setInputId(id); saveInputId(id) }
  const changeOutput = (id) => { setOutputIdSt(id); saveOutputId(id) }
  const changeModel  = (m)  => {
    setModelSt(m); saveModel(m)
    if (outputRef.current) {
      clearAllLeds(outputRef.current)
      Object.entries(mappings).forEach(([k, v]) => sendLED(outputRef.current, k, normalizeMapping(v).led_idle, m))
    }
  }

  const bindTo = (actionKey) => {
    if (!captured) return
    const next = { ...mappings, [captured.key]: normalizeMapping({ action: actionKey, led_idle: 'off', led_active: 'green' }) }
    setMappings(next); saveMappings(next)
    setCaptured(null); setLearnMode(false)
  }
  const updateLED = (key, field, color) => {
    const cur = normalizeMapping(mappings[key])
    const next = { ...mappings, [key]: { ...cur, [field]: color } }
    setMappings(next); saveMappings(next)
    if (outputRef.current) sendLED(outputRef.current, key, color)
  }
  const removeMapping = (key) => {
    const next = { ...mappings }
    delete next[key]
    setMappings(next); saveMappings(next)
    if (outputRef.current) sendLED(outputRef.current, key, 'off')
  }
  const clearAll = async () => {
    if (!(await confirm('Alle MIDI mappings wissen?'))) return
    if (outputRef.current) clearAllLeds(outputRef.current)
    setMappings({}); saveMappings({})
  }
  const testAllIdle   = () => { if (outputRef.current) Object.entries(mappings).forEach(([k, v]) => sendLED(outputRef.current, k, normalizeMapping(v).led_idle)) }
  const testAllActive = () => { if (outputRef.current) Object.entries(mappings).forEach(([k, v]) => sendLED(outputRef.current, k, normalizeMapping(v).led_active)) }
  const testOff       = () => { if (outputRef.current) clearAllLeds(outputRef.current) }

  const mappingsList = Object.entries(mappings)
  const actionByKey = Object.fromEntries(ACTIONS.map(a => [a.key, a]))

  return (
    <div className="max-w-5xl mx-auto space-y-4">
      <KempenvisionNav />
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-[4px] border border-border bg-card flex items-center justify-center" style={{ borderLeft: "2px solid rgb(var(--acid-rgb))" }}>
            <Zap className="h-5 w-5 text-white" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Kempenvision · MIDI</div>
            <h1 className="text-xl font-bold">Akai / MIDI configuratie</h1>
          </div>
        </div>
        <Button asChild variant="ghost" size="sm">
          <Link to="/kempenvision"><ArrowLeft className="h-4 w-4 mr-1" />Terug</Link>
        </Button>
      </div>


      <MidiSyncCard sync={midiSync} scopeLabel="Kempenvision" />
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Verbinding</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {!access && (
            <div className="flex items-center gap-3">
              <Button onClick={openAccess}><Plug className="h-4 w-4 mr-2" />MIDI toestemming vragen</Button>
              <div className="text-sm text-muted-foreground">Vereist Chrome of Edge. Sluit je Akai aan via USB voor je klikt.</div>
            </div>
          )}
          {access && (
            <>
              <div className="flex items-center gap-3 flex-wrap">
                <PlugZap className="h-5 w-5 text-emerald-400" />
                <span className="text-sm w-20">Input:</span>
                <Select value={inputId ?? ''} onValueChange={changeInput}>
                  <SelectTrigger className="w-64"><SelectValue placeholder="Kies input" /></SelectTrigger>
                  <SelectContent>
                    {inputs.map(i => <SelectItem key={i.id} value={i.id}>{i.name}{i.manufacturer ? ` (${i.manufacturer})` : ''}</SelectItem>)}
                  </SelectContent>
                </Select>
                {lastEvent && (
                  <Badge variant="outline" className="border-cyan-500/40 text-cyan-400 font-mono">
                    {eventLabel(lastEvent)}
                  </Badge>
                )}
              </div>
              {modelHasLeds(model) && (
                <div className="flex items-center gap-3 flex-wrap">
                  <Lightbulb className="h-5 w-5 text-yellow-400" />
                  <span className="text-sm w-20">LED output:</span>
                  <Select value={outputId ?? ''} onValueChange={changeOutput}>
                    <SelectTrigger className="w-64"><SelectValue placeholder="Kies output" /></SelectTrigger>
                    <SelectContent>
                      {outputs.map(o => <SelectItem key={o.id} value={o.id}>{o.name}{o.manufacturer ? ` (${o.manufacturer})` : ''}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button size="sm" variant="outline" onClick={testAllIdle}   disabled={!outputId}>Test idle</Button>
                  <Button size="sm" variant="outline" onClick={testAllActive} disabled={!outputId}>Test active</Button>
                  <Button size="sm" variant="outline" onClick={testOff}       disabled={!outputId}>LEDs uit</Button>
                </div>
              )}
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-sm w-28">Device:</span>
                <Select value={model} onValueChange={changeModel}>
                  <SelectTrigger className="w-64"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DEVICE_MODEL_KEYS.map(k => <SelectItem key={k} value={k}>{DEVICE_MODELS[k].label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <span className="text-xs text-muted-foreground">Kies mk2 als de LEDs zwak of zonder kleur zijn bij mk1. LPD8 heeft geen LEDs — enkel input.</span>
              </div>
            </>
          )}
          {error && <div className="text-sm text-red-500">⚠ {error}</div>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-sm">Nieuwe mapping</CardTitle>
          {mappingsList.length > 0 && (
            <Button variant="ghost" size="sm" onClick={clearAll}>
              <Trash2 className="h-4 w-4 mr-1 text-red-500" />Alles wissen
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          {!captured && (
            <div className="flex items-center gap-3">
              <Button variant={learnMode ? 'default' : 'outline'} onClick={() => setLearnMode(l => !l)} disabled={!access}>
                <Plus className="h-4 w-4 mr-2" />{learnMode ? 'Wacht op MIDI pad…' : 'Nieuwe binding'}
              </Button>
              {learnMode && <span className="text-sm text-muted-foreground animate-pulse">Druk een knop of fader op je Akai…</span>}
            </div>
          )}
          {captured && (
            <div>
              <div className="mb-3 text-sm">
                Gedetecteerd: <b className="font-mono text-cyan-400">{keyLabel(captured.key)}</b>. Kies een actie:
                <Button variant="ghost" size="sm" onClick={() => { setCaptured(null); setLearnMode(false) }} className="ml-3">
                  Annuleer
                </Button>
              </div>
              <div className="grid gap-3">
                {ACTION_GROUPS.map(g => (
                  <div key={g}>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-1">{g}</div>
                    <div className="flex gap-1.5 flex-wrap">
                      {ACTIONS.filter(a => a.group === g).map(a => (
                        <Button key={a.key} size="sm" variant="outline" onClick={() => bindTo(a.key)}>
                          {a.label.replace(`${g}: `, '')}
                        </Button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Bestaande mappings ({mappingsList.length})</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {mappingsList.length === 0 && <p className="text-sm text-muted-foreground">Nog geen bindings. Klik "Nieuwe binding" en druk een pad.</p>}
          {mappingsList.length > 0 && (
            <div className="grid grid-cols-[10rem_1fr_9rem_9rem_2rem] gap-2 items-center text-xs text-muted-foreground uppercase tracking-widest px-1">
              <span>Pad</span><span>Actie</span><span>LED idle</span><span>LED active</span><span></span>
            </div>
          )}
          {mappingsList.map(([key, raw]) => {
            const m = normalizeMapping(raw)
            const a = actionByKey[m.action]
            const isNote = keyIsNote(key)
            const canLed = isNote && modelHasLeds(model)
            return (
              <div key={key} className="grid grid-cols-[10rem_1fr_9rem_9rem_2rem] gap-2 items-center p-2 rounded border border-border bg-card">
                <span className="font-mono text-sm text-cyan-400 truncate">{keyLabel(key)}</span>
                <span className="text-sm truncate">{a?.label ?? m.action}</span>
                <LedSelect value={m.led_idle}   disabled={!canLed} onChange={c => updateLED(key, 'led_idle', c)} />
                <LedSelect value={m.led_active} disabled={!canLed} onChange={c => updateLED(key, 'led_active', c)} />
                <Button variant="ghost" size="sm" onClick={() => removeMapping(key)}>
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>
            )
          })}
        </CardContent>
      </Card>

      <Card className="border-cyan-500/40 bg-cyan-500/5">
        <CardContent className="p-4 text-sm space-y-2">
          <div className="font-semibold text-cyan-400">Hoe activeren tijdens de show:</div>
          <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
            <li>Open <Link to="/kempenvision/control" className="text-cyan-400 underline">Control</Link></li>
            <li>Klik het bliksem-icoon rechtsboven om MIDI besturing aan te zetten</li>
            <li>Mappings die hier zijn gemaakt werken dan direct — druk pads om acties te triggeren</li>
          </ol>
          <p className="text-xs pt-2">Mappings zijn opgeslagen per browser (localStorage). Wisselen van machine? Zet zelfde bindings opnieuw of exporteer/importeer JSON via <code>localStorage.kempenvision-midi-mappings</code>.</p>
          <p className="text-xs">
            LEDs volgen automatisch: "active" kleur brandt als de <b>view</b> of <b>phase</b> matcht met de mapping. APC Mini kleuren: groen · rood · geel (met knipper-varianten).
          </p>
        </CardContent>
      </Card>
    </div>
  )
}

function LedSelect({ value, disabled, onChange }) {
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className="h-8 text-xs">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {LED_COLOR_KEYS.map(c => (
          <SelectItem key={c} value={c}>
            <span className="flex items-center gap-2">
              <span className="inline-block w-3 h-3 rounded-full border border-black/30"
                    style={{ background: LED_COLORS[c].swatch, opacity: c === 'off' ? 0.4 : 1 }} />
              {LED_COLORS[c].label}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
